import React from 'react';

import { SquarePen } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

import AppModal from '../../../ui-components/AppModal';
import BaseList from '../../../ui-components/BaseList';
import { STATUS } from '../../../utils/constant-value';

import {
  useLazyGetPermissionListQuery,
  useGetRoleListQuery,
  useUpdateRoleMutation,
  useLazyGetRoleByIdQuery,
  useCreateRoleMutation,
} from './roleApi';

import type { Roles } from './model';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';

const transformData = (records: Roles[]): Roles[] =>
  records.map((data) => ({ ...data }));

const createRoleNameRenderer = (
  navigate: (path: string) => void,
  fetchPermissions: any,
  getRoleById: any
) => {
  const RoleNameRenderer = (params: {
    value: { groupId: number; groupName: string };
  }) => {
    const { groupId, groupName } = params.value;
    return (
      <button
        type="button"
        onClick={async (e) => {
          try {
            await fetchPermissions().unwrap();
            await getRoleById(groupId).unwrap();
            navigate(`/roles/${groupId}`);
          } catch (error: unknown) {
            e.preventDefault();
          }
        }}
        className="text-brand-600 hover:underline hover:text-brand-800 cursor-pointer transition font-semibold"
      >
        {groupName}
      </button>
    );
  };

  RoleNameRenderer.displayName = 'RoleNameRenderer';
  return RoleNameRenderer;
};

const StatusRenderer = (params: { value: string }) => (
  <span className="bg-success-600 dark:bg-success-500 text-white text-sm px-3 py-1 rounded">
    {params.value}
  </span>
);

StatusRenderer.displayName = 'StatusRenderer';

const createActionRenderer = (setSelectedRole: any, navigate: any) => {
  const ActionRenderer = (params: { data: Roles }) => {
    const rowData = params.data;
    if (!rowData?.groupId) return null;

    return (
      <button
        type="button"
        onClick={() => {
          setSelectedRole(rowData);
          navigate('.', { replace: true });
        }}
        className="
          bg-white cursor-pointer dark:bg-white border border-brand-600 text-brand-600
          hover:bg-brand-600 hover:text-white dark:hover:text-white
          transition-colors duration-200 rounded p-1 my-1 mx-1
        "
        title="Edit"
      >
        <SquarePen />
      </button>
    );
  };

  ActionRenderer.displayName = 'ActionRenderer';
  return ActionRenderer;
};

const RolePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [fetchPermissions] = useLazyGetPermissionListQuery();
  const [updateRole] = useUpdateRoleMutation();
  const [createRole] = useCreateRoleMutation();
  const [getRoleById] = useLazyGetRoleByIdQuery();

  const modalOpen = new URLSearchParams(location.search).get('new') === 'true';

  const [selectedRole, setSelectedRole] = React.useState<Roles | null>(null);
  const isEditMode = selectedRole !== null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { groupName: '', status: 'Active' },
  });

  React.useEffect(() => {
    if (isEditMode && selectedRole) {
      reset({
        groupName: selectedRole.groupName || '',
        status: selectedRole.status || 'Active',
      });
    }
  }, [isEditMode, selectedRole, reset]);

  const columns: ColDef<Roles>[] = [
    {
      headerName: 'Name',
      field: 'groupName',
      unSortIcon: true,
      headerClass: 'font-bold',
      valueGetter: (params: ValueGetterParams<Roles>) => ({
        groupId: params.data?.groupId ?? 0,
        groupName: params.data?.groupName ?? '',
      }),
      cellRenderer: createRoleNameRenderer(
        navigate,
        fetchPermissions,
        getRoleById
      ),
    },
    {
      headerName: 'Status',
      field: 'status',
      headerClass: 'font-bold',
      sortable: false,
      cellRenderer: StatusRenderer,
    },
    {
      headerName: 'Action',
      field: 'groupId',
      headerClass: 'font-bold',
      sortable: false,
      cellRenderer: createActionRenderer(setSelectedRole, navigate),
    },
  ];

  const onSubmit = async (data: { groupName: string; status: string }) => {
    try {
      if (isEditMode && selectedRole) {
        await updateRole({
          id: selectedRole.groupId,
          groupName: data.groupName,
          status: data.status,
        }).unwrap();
      } else {
        await createRole({
          groupName: data.groupName,
          status: data.status,
        }).unwrap();
      }

      setSelectedRole(null);
      reset({ groupName: '', status: 'Active' });
      navigate('.', { replace: true });
    } catch (err: unknown) {
      console.error(err);
    }
  };

  return (
    <>
      <BaseList
        title={{
          singular: 'Role',
          plural: 'Role List',
        }}
        fetchData={useGetRoleListQuery}
        transformData={transformData}
        columns={columns}
        showNewBtn={true}
        enableCheckbox={false}
        addNewLink="?new=true"
        headerMargin="mb-3 mt-0"
      />

      {(modalOpen || isEditMode) && (
        <AppModal
          isOpen={true}
          title="Manage Role"
          onClose={() => {
            setSelectedRole(null);
            reset({ groupName: '', status: 'Active' });
            navigate('.', { replace: true });
          }}
          formId="role-form"
          actions={[
            {
              label: 'Save',
              type: 'submit',
              variant: 'primary',
            },
            {
              label: 'Cancel',
              type: 'button',
              variant: 'secondary',
              onClick: () => {
                setSelectedRole(null);
                reset({ groupName: '', status: 'Active' });
                navigate('.', { replace: true });
              },
            },
          ]}
        >
          <form
            id="role-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5"
          >
            <div>
              <label className="label">
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                className={`input ${
                  errors.groupName
                    ? 'border-error-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Name"
                {...register('groupName', {
                  required: 'Name is required',
                })}
                autoFocus
              />
              {errors.groupName && (
                <p className="error-msg">{errors.groupName.message}</p>
              )}
            </div>

            {!isEditMode && (
              <div className="mb-4">
                <span className="label">
                  Status <span className="required">*</span>
                </span>
                {STATUS.map((item, i) => (
                  <label
                    key={item.value}
                    htmlFor={`status-${i}`}
                    className="radio-label"
                  >
                    <input
                      {...register('status')}
                      type="radio"
                      id={`status-${i}`}
                      value={item.value}
                      className="mr-1"
                    />
                    {item.label}
                  </label>
                ))}
              </div>
            )}
          </form>
        </AppModal>
      )}
    </>
  );
};

export default RolePage;
